/* iter55f58 — Advanced Modeling → Character Identity panel.
 *
 * Holds the technical identity fields that used to live at the top of
 * the Character Bible: Name / Role / Age / Archetype text + the 77-
 * archetype dropdown that triggers a Claude re-tune of the character.
 *
 * Kept out of the main Bible so casual users see a prose-focused view;
 * professionals flip on Advanced Modeling → Character Identity to get
 * the full control surface.
 *
 * Everything is wired to the same `usePW()` store + backend endpoints
 * as before, so edits round-trip correctly regardless of which panel
 * the user made them in.
 */
import React from 'react';
import { usePW } from '@/lib/store';
import { ARCHETYPES, ARCHETYPE_GROUPS } from '@/lib/archetypes';
import { ArchetypePickerDialog } from './character-bible/ArchetypePickerDialog';
import { charactersApi } from '@/lib/api';
import { PanelFrame } from '../workspace/PanelFrame';
import { Users, X, Sparkles, Loader2, Maximize2, Wand2, BookOpen } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function Field({ label, value, onBlur, type = 'text', testId, onAutoFill, autoFilling }) {
  const [v, setV] = React.useState(value ?? '');
  const focused = React.useRef(false);
  // Only sync from parent when the input is NOT focused — prevents the
  // useEffect from resetting the cursor position mid-keystroke when a
  // background state update (e.g. updateCharacter) propagates down.
  React.useEffect(() => {
    if (!focused.current) setV(value ?? '');
  }, [value]);
  return (
    <div>
      <div className="flex items-center gap-1">
        <Label className="text-[13px] uppercase tracking-wider text-muted-foreground">{label}</Label>
        {onAutoFill && (
          <button
            type="button"
            onClick={onAutoFill}
            disabled={autoFilling}
            title={`Let Claude estimate this character's ${label.toLowerCase()}`}
            data-testid={`${testId}-autofill`}
            className="ml-auto flex items-center gap-0.5 rounded-sm px-1 py-0.5 text-[9.5px] text-muted-foreground/60 transition-colors hover:text-primary disabled:opacity-40"
          >
            {autoFilling
              ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
              : <Wand2 className="h-2.5 w-2.5" />}
            {autoFilling ? 'Thinking…' : 'Suggest'}
          </button>
        )}
      </div>
      <Input
        type={type}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; onBlur(v); }}
        data-testid={testId}
        className="mt-1 h-8 text-[14px]"
      />
    </div>
  );
}

/* Shared editable form — used both in the inline panel and the dialog. */
function IdentityForm({ ch, characters, onSelectCharacter, onPersistField, onPickArchetype, onSuggestArchetype, suggesting, onSuggestAge, suggestingAge }) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  return (
    <div className="flex flex-col gap-3">
      {/* Character selector */}
      <div>
        <Label className="text-[13px] uppercase tracking-wider text-muted-foreground">Selected character</Label>
        <select
          value={ch.id}
          onChange={(e) => onSelectCharacter(e.target.value)}
          data-testid="advanced-character-identity-select"
          className="mt-1 w-full rounded-sm border border-panel-border bg-background px-2 py-1 text-[14px] outline-none focus:border-primary"
        >
          {characters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{c.role ? ` · ${c.role}` : ''}{c.archetype ? ` · ${c.archetype}` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-md border border-panel-border bg-card p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-[13px] uppercase tracking-wider text-muted-foreground shrink-0">Identity</span>
          <div className="flex items-center gap-1.5">
            {/* Archetype picker — opens the full ArchetypePickerDialog */}
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="advanced-character-archetype-picker"
              className="flex min-w-0 flex-1 items-center gap-1.5 truncate rounded-sm border border-panel-border bg-background px-2 py-0.5 text-[13px] text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
              title="Browse all 93 archetypes with definitions"
            >
              <BookOpen className="h-3 w-3 shrink-0" />
              <span className="truncate">{ch.archetype || 'Choose archetype…'}</span>
            </button>
            {/* AI suggest */}
            <button
              type="button"
              onClick={onSuggestArchetype}
              disabled={suggesting}
              title="Let Claude propose the best archetype for this character"
              data-testid="suggest-archetype-btn"
              className="flex shrink-0 items-center gap-1 rounded-sm border border-panel-border bg-background px-2 py-0.5 text-[13px] text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50"
            >
              {suggesting
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Wand2 className="h-3 w-3" />}
              <span>{suggesting ? 'Thinking…' : 'Suggest'}</span>
            </button>
          </div>
        </div>
        <ArchetypePickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          currentArchetype={ch.archetype}
          onSelect={(arc) => { onPickArchetype(arc); }}
          onAiSuggest={onSuggestArchetype}
          suggesting={suggesting}
        />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Name"      value={ch.name}        onBlur={(v) => onPersistField('name', v)}      testId="advanced-character-name" />
          <Field label="Role"      value={ch.role}        onBlur={(v) => onPersistField('role', v)}      testId="advanced-character-role" />
          <Field label="Age"       value={String(ch.age)} onBlur={(v) => onPersistField('age', v)}       testId="advanced-character-age" type="number" onAutoFill={onSuggestAge} autoFilling={suggestingAge} />
          <Field label="Archetype" value={ch.archetype}   onBlur={(v) => onPersistField('archetype', v)} testId="advanced-character-archetype-input" />
        </div>
      </div>
    </div>
  );
}

export default function AdvancedCharacterIdentityPanel({ onClose }) {
  const { characters, activeCharacterId, setActiveCharacterId, updateCharacter } = usePW();
  const ch = characters.find((c) => c.id === activeCharacterId) || characters[0];

  const [pending, setPending]   = React.useState(null);
  const [applying, setApplying] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [suggesting, setSuggesting] = React.useState(false);
  const [suggestingAge, setSuggestingAge] = React.useState(false);

  const suggestArchetype = async () => {    if (!ch || suggesting) return;
    setSuggesting(true);
    try {
      const result = await charactersApi.suggestArchetype(ch.id, ARCHETYPES);
      // result = { id, name, blurb, reasoning }
      // Find the full archetype object so the confirmation dialog shows
      // the blurb and reasoning correctly.
      const arc = ARCHETYPES.find((a) => a.id === result.id) || {};
      setPending({
        id:        result.id   || arc.id   || '',
        name:      result.name || arc.name || '',
        blurb:     result.blurb || arc.blurb || '',
        reasoning: result.reasoning || '',
      });
      toast.success(`Claude suggests: ${result.name}`, {
        description: result.reasoning || 'Click Commit to apply.',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not suggest archetype — try again.');
    } finally {
      setSuggesting(false);
    }
  };

  const suggestAge = async () => {
    if (!ch || suggestingAge) return;
    setSuggestingAge(true);
    try {
      const result = await charactersApi.suggestAge(ch.id);
      await persistField('age', String(result.age));
      toast.success(`Age set to ${result.age}`, {
        description: result.reasoning || '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not suggest age — try again.');
    } finally {
      setSuggestingAge(false);
    }
  };


  const persistField = async (field, value) => {
    if (!ch) return;
    const parsed = (field === 'age') ? Number.parseInt(value, 10) || 0 : value;
    updateCharacter(ch.id, { [field]: parsed });
    try {
      await axios.put(`${API}/characters/${ch.id}`, { [field]: parsed });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not save ${field}.`);
    }
  };

  const applyArchetype = async () => {
    if (!ch || !pending) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/characters/${ch.id}/apply-archetype`, {
        archetype_id:    pending.id,
        archetype_name:  pending.name,
        archetype_blurb: pending.blurb,
      });
      updateCharacter(ch.id, {
        archetype:   r.data.archetype,
        description: r.data.description,
        traits:      r.data.traits,
      });
      toast.success(`Archetype committed: ${pending.name}`,
                     { description: 'Description & traits re-tuned by Claude.' });
      setPending(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply archetype — try again.');
    } finally { setApplying(false); }
  };

  return (
    <>
    <PanelFrame
      title={<span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Character Identity <span className="text-[9px] uppercase tracking-wider text-primary/80">Advanced</span></span>}
      onClose={onClose}
      testId="advanced-character-identity-panel"
      actions={
        <button
          type="button"
          onClick={() => setExpanded(true)}
          title="Open full-screen editor"
          data-testid="advanced-character-identity-expand"
          className="flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Maximize2 className="h-3 w-3" />
        </button>
      }
    >
      {!ch && (
        <div className="p-4 text-[13.5px] text-muted-foreground" data-testid="advanced-character-identity-empty">
          No characters yet. Add one from the Character Bible first.
        </div>
      )}
      {ch && (
        /* Inline panel — scrollable so it's usable even when the ResizablePanel
           slot is small. The expand button opens the full dialog. */
        <div className="h-full overflow-y-auto scroll-thin p-3">
          <IdentityForm
            ch={ch}
            characters={characters}
            onSelectCharacter={setActiveCharacterId}
            onPersistField={persistField}
            onPickArchetype={setPending}
            onSuggestArchetype={suggestArchetype}
            suggesting={suggesting}
            onSuggestAge={suggestAge}
            suggestingAge={suggestingAge}
          />
        </div>
      )}
    </PanelFrame>

    {/* ── Full-screen dialog ── */}
    <Dialog open={expanded} onOpenChange={setExpanded}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col"
        data-testid="advanced-character-identity-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Character Identity
            {ch && (
              <span className="text-[13.5px] font-normal text-muted-foreground">
                · {ch.name}
              </span>
            )}
            <span className="ml-1 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-primary">
              Advanced
            </span>
          </DialogTitle>
          <DialogDescription className="text-[13.5px]">
            Edit name, role, age, and archetype for any character in this project.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto scroll-thin py-1 pr-1">
          {ch ? (
            <IdentityForm
              ch={ch}
              characters={characters}
              onSelectCharacter={setActiveCharacterId}
              onPersistField={persistField}
              onPickArchetype={setPending}
              onSuggestArchetype={suggestArchetype}
              suggesting={suggesting}
              onSuggestAge={suggestAge}
              suggestingAge={suggestingAge}
            />
          ) : (
            <p className="text-[13.5px] text-muted-foreground p-2">
              No characters yet. Add one from the Character Bible first.
            </p>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-panel-border pt-3">
          <Button variant="outline" onClick={() => setExpanded(false)} data-testid="advanced-character-identity-dialog-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Archetype confirmation modal */}
    <Dialog open={!!pending} onOpenChange={(v) => { if (!v) setPending(null); }}>
      <DialogContent className="max-w-md" data-testid="advanced-archetype-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-primary" /> Commit archetype?
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{pending?.name}</span>
            {' — '}
            {pending?.blurb || 'Claude will re-tune the character description & trait scores to match.'}
            {pending?.reasoning && (
              <span className="mt-1 block text-[13px] text-muted-foreground italic">
                "{pending.reasoning}"
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setPending(null)} data-testid="advanced-archetype-cancel">
            <X className="mr-1 h-3.5 w-3.5" /> Cancel
          </Button>
          <Button onClick={applyArchetype} disabled={applying} data-testid="advanced-archetype-commit">
            {applying ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}
            Commit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
